import React, { useCallback, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { toast } from "sonner";

import { cn } from "~/common/redesign-v1/cn";
import { HeadCell, RdTable } from "~/common/redesign-v1/components/rd-table";
import { ChevronDown, CircleCheck, Eye, FileArchive, FileText, Link as LinkIcon } from "~/common/redesign-v1/icons";
import { Badge } from "~/common/redesign-v1/ui/badge";
import { buttonVariants, Button as RedesignButton } from "~/common/redesign-v1/ui/button";
import { Popover, PopoverAnchor, PopoverContent } from "~/common/redesign-v1/ui/popover";
import { PatientPlanExpectedDate } from "~/components/patient/patient_show/patient_plan_expected_date";
import { ViewCard } from "~/components/redesign-v1/patient-view/view-primitives";
import { Button } from "~/components/ui/button";

import { ReviewDialog } from "./review-dialog";
import type { DoctorViewerApi, ViewerPlan } from "./types";
import styles from "./viewer.module.css";

type Props = {
  patientId: number;
  doctorName: string;
  clinicTitle: string;
  api: DoctorViewerApi;
  onRefreshPatient: () => Promise<void>;
  inline?: boolean;
  legacyActions?: React.ReactNode;
  legacyArchiveActions?: React.ReactNode;
  legacyPlanDate?: string | null;
  expectedPlanDate?: string | null;
  openInNewWindow?: boolean;
  initialPlanId?: number;
};

export function ViewerCard({
  patientId,
  doctorName,
  clinicTitle,
  api,
  onRefreshPatient,
  inline,
  legacyActions,
  legacyArchiveActions,
  legacyPlanDate,
  expectedPlanDate,
  openInNewWindow,
  initialPlanId,
}: Props) {
  const intl = useIntl();
  const RetryButton = inline ? RedesignButton : Button;
  const [plans, setPlans] = useState<ViewerPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [revision, setRevision] = useState(0);
  const opening = useRef<AbortController>();
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [sharingId, setSharingId] = useState<number | null>(null);
  const [copiedPlanId, setCopiedPlanId] = useState<number | null>(null);
  const copyNoticeTimeout = useRef<number>();
  const close = useCallback(() => {
    if (initialPlanId != null) window.close();
    setSelectedId(null);
  }, [initialPlanId]);
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setFailed(false);
    setPlans([]);
    setSelectedId(null);
    setShowAll(false);
    setCopiedPlanId(null);
    void api
      .list(patientId, controller.signal)
      .then((result) => {
        if (!controller.signal.aborted) {
          setPlans(result);
          if (initialPlanId != null && result.some((plan) => plan.id === initialPlanId)) setSelectedId(initialPlanId);
        }
      })
      .catch(() => {
        if (!controller.signal.aborted) setFailed(true);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => {
      controller.abort();
      opening.current?.abort();
      window.clearTimeout(copyNoticeTimeout.current);
    };
  }, [patientId, api, revision, initialPlanId]);
  const sharePlan = async (planId: number) => {
    setSharingId(planId);
    try {
      const url = await api.share(patientId, planId);
      await navigator.clipboard.writeText(url);
      setCopiedPlanId(planId);
      window.clearTimeout(copyNoticeTimeout.current);
      copyNoticeTimeout.current = window.setTimeout(() => setCopiedPlanId(null), 1800);
    } catch {
      toast.error(intl.formatMessage({ id: "viewer.share.error" }), { duration: 3000, position: "bottom-center" });
    } finally {
      setSharingId(null);
    }
  };
  const openViewer = async (planId?: number) => {
    const controller = new AbortController();
    opening.current?.abort();
    opening.current = controller;
    setLoading(true);
    setFailed(false);
    try {
      const result = await api.list(patientId, controller.signal);
      if (controller.signal.aborted) return;
      setPlans(result);
      const selected = planId ?? result[0]?.id;
      if (result.some((plan) => plan.id === selected)) setSelectedId(selected ?? null);
    } catch {
      if (!controller.signal.aborted) setFailed(true);
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  };
  const dialog =
    selectedId !== null ? (
      <ReviewDialog
        key={patientId}
        patientId={patientId}
        initialPlans={plans}
        initialPlanId={selectedId}
        api={api}
        doctorName={doctorName}
        clinicTitle={clinicTitle}
        onRefreshPatient={onRefreshPatient}
        onPlansChanged={(nextPlans) => {
          setPlans(nextPlans);
          setShowAll(false);
        }}
        onClose={close}
      />
    ) : null;
  const orderedPlans = [...plans].sort((a, b) => b.correction_index - a.correction_index || b.version - a.version);
  const latestPlan = orderedPlans[0];
  const approvedPlans = orderedPlans
    .filter((plan) => plan.state === "approved")
    .sort((a, b) => Date.parse(b.approved_at ?? "") - Date.parse(a.approved_at ?? "") || b.correction_index - a.correction_index || b.version - a.version);
  const currentPlan = (latestPlan?.state === "pending" ? latestPlan : undefined) ?? approvedPlans[0] ?? latestPlan;
  const currentPlans = currentPlan ? [currentPlan] : [];
  const archivedPlans = orderedPlans
    .filter((plan) => plan !== currentPlan)
    .sort((a, b) => Date.parse(b.published_at ?? "") - Date.parse(a.published_at ?? "") || b.correction_index - a.correction_index || b.version - a.version);
  const archivedLegacyPlan = inline && Boolean(legacyActions) && plans.length > 0;
  const archiveCount = archivedPlans.length + (archivedLegacyPlan ? 1 : 0);
  const archived = archiveCount > 0;
  const formatPlanDate = (date: string) => intl.formatDate(date, { day: "2-digit", month: "2-digit", year: "numeric" });
  const versionsTable = (versions: ViewerPlan[], archive = false) => {
    const actionClassName = inline ? cn(buttonVariants({ variant: "secondary", size: archive ? "sm" : "default" }), archive ? "tw-size-8 tw-p-0" : "max-[640px]:tw-px-3") : undefined;
    const tableContent = (
      <>
        <caption className="tw-sr-only">{intl.formatMessage({ id: "viewer.versions" })}</caption>
        {archive ? (
          <colgroup>
            <col />
            <col />
            <col />
            <col />
          </colgroup>
        ) : null}
        <thead className={archive ? styles["archiveHead"] : "tw-sr-only"}>
          <tr>
            <HeadCell labelId={archive ? "viewer.plan.name" : "viewer.version"} />
            {archive ? <HeadCell labelId="viewer.date" /> : null}
            {archive ? <HeadCell labelId="viewer.status" /> : null}
            <HeadCell labelId="viewer.actions" />
          </tr>
        </thead>
        <tbody>
          {versions.map((plan) => (
            <tr key={plan.id}>
              <td>
                {intl.formatMessage({ id: "viewer.version.option" }, { correction: plan.correction_index, version: plan.version })}
                {!archive ? (
                  <Badge variant="secondary" className={cn("tw-ml-2", plan.state === "approved" ? "tw-bg-green-100 tw-text-green-800" : "tw-text-muted-foreground")}>
                    {intl.formatMessage({ id: `viewer.state.${plan.state}` })}
                  </Badge>
                ) : null}
                {!archive && (plan.published_at || plan.submitted_at) ? (
                  <small className={styles["planDate"]}>
                    {plan.state === "pending" && plan.submitted_at ? (
                      intl.formatMessage({ id: "viewer.available.since" }, { date: formatPlanDate(plan.submitted_at) })
                    ) : plan.published_at ? (
                      <time dateTime={plan.published_at}>{formatPlanDate(plan.published_at)}</time>
                    ) : null}
                  </small>
                ) : null}
              </td>
              {archive ? <td className={styles["archiveDate"]}>{plan.published_at ? <time dateTime={plan.published_at}>{formatPlanDate(plan.published_at)}</time> : "—"}</td> : null}
              {archive ? (
                <td>
                  <Badge variant="secondary" className={plan.state === "approved" ? "tw-bg-green-100 tw-text-green-800" : "tw-text-muted-foreground"}>
                    {intl.formatMessage({ id: `viewer.state.${plan.state === "not_approved" ? "rework" : plan.state}` })}
                  </Badge>
                </td>
              ) : null}
              <td>
                <div className={[styles["versionActions"], !archive ? styles["currentVersionActions"] : undefined].filter(Boolean).join(" ")}>
                  {openInNewWindow ? (
                    <a
                      className={actionClassName}
                      href={`/pages/patient/${patientId}?viewer_plan=${plan.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={intl.formatMessage({ id: "viewer.open.selected" }, { correction: plan.correction_index, version: plan.version })}
                      title={intl.formatMessage({ id: "viewer.open.version" })}
                    >
                      <Eye aria-hidden="true" className="tw-size-4" />
                      {!archive ? <span>{intl.formatMessage({ id: "viewer.watch" })}</span> : null}
                    </a>
                  ) : (
                    <button
                      className={actionClassName}
                      type="button"
                      disabled={loading}
                      onClick={() => {
                        void openViewer(plan.id);
                      }}
                      aria-label={intl.formatMessage({ id: "viewer.open.selected" }, { correction: plan.correction_index, version: plan.version })}
                      title={intl.formatMessage({ id: "viewer.open.version" })}
                    >
                      <Eye aria-hidden="true" className="tw-size-4" />
                      {!archive ? <span>{intl.formatMessage({ id: "viewer.watch" })}</span> : null}
                    </button>
                  )}
                  <Popover
                    open={copiedPlanId === plan.id}
                    onOpenChange={(open) => {
                      if (!open) setCopiedPlanId(null);
                    }}
                  >
                    <PopoverAnchor asChild>
                      <button
                        className={actionClassName}
                        type="button"
                        disabled={sharingId !== null}
                        onClick={() => void sharePlan(plan.id)}
                        aria-label={intl.formatMessage({ id: "viewer.share.selected" }, { correction: plan.correction_index, version: plan.version })}
                        title={intl.formatMessage({ id: "pat.plan.link" })}
                      >
                        <LinkIcon aria-hidden="true" className="tw-size-4" />
                        {!archive ? <span>{intl.formatMessage({ id: "viewer.link" })}</span> : null}
                      </button>
                    </PopoverAnchor>
                    <PopoverContent
                      side="top"
                      align="center"
                      style={{ position: "relative", left: "-12px" }}
                      sideOffset={8}
                      className="tw-w-max tw-max-w-[calc(100vw-24px)] tw-rounded-xl tw-px-3 tw-py-2 tw-text-xs"
                      onOpenAutoFocus={(event) => event.preventDefault()}
                      onCloseAutoFocus={(event) => event.preventDefault()}
                    >
                      <div role="status" className="tw-flex tw-items-center tw-gap-2">
                        <CircleCheck aria-hidden="true" className="tw-size-4 tw-shrink-0" />
                        {intl.formatMessage({ id: "notify.link_copy" })}
                      </div>
                    </PopoverContent>
                  </Popover>
                  <a
                    className={actionClassName}
                    href={`/pages/patient/${patientId}/report?plan=${plan.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={intl.formatMessage({ id: "viewer.report.selected" }, { correction: plan.correction_index, version: plan.version })}
                    title={intl.formatMessage({ id: "viewer.report" })}
                  >
                    <FileText aria-hidden="true" className="tw-size-4" />
                    {!archive ? <span>{intl.formatMessage({ id: "viewer.report" })}</span> : null}
                  </a>
                </div>
              </td>
            </tr>
          ))}
          {archive && archivedLegacyPlan ? (
            <tr aria-label={intl.formatMessage({ id: "viewer.previous.plan" })}>
              <td>{intl.formatMessage({ id: "viewer.legacy.plan" })}</td>
              <td className={styles["archiveDate"]}>{legacyPlanDate ? <time dateTime={legacyPlanDate}>{formatPlanDate(legacyPlanDate)}</time> : "—"}</td>
              <td>
                <Badge variant="secondary" className="tw-text-muted-foreground">
                  {intl.formatMessage({ id: "viewer.state.approved" })}
                </Badge>
              </td>
              <td>
                <div className={styles["versionActions"]}>{legacyArchiveActions ?? legacyActions}</div>
              </td>
            </tr>
          ) : null}
        </tbody>
      </>
    );
    return (
      <div className={[styles["versions"], archive ? styles["archiveVersions"] : styles["currentVersions"]].filter(Boolean).join(" ")}>
        {archive ? <RdTable containerClassName="tw-rounded-lg">{tableContent}</RdTable> : <table aria-label={intl.formatMessage({ id: "viewer.versions" })}>{tableContent}</table>}
      </div>
    );
  };
  const content = (
    <>
      <PatientPlanExpectedDate date={expectedPlanDate ?? null} />
      {loading ? <p role="status">{intl.formatMessage({ id: "viewer.loading" })}</p> : null}
      {failed ? (
        <p role="alert">
          {intl.formatMessage({ id: "viewer.list.error" })}{" "}
          <RetryButton variant="secondary" className="tw-ml-2" onClick={() => setRevision((value) => value + 1)}>
            {intl.formatMessage({ id: "viewer.retry" })}
          </RetryButton>
        </p>
      ) : null}
      {currentPlans.length > 0 || archived ? (
        <div className={[styles["planSection"], inline ? styles["inlinePlanSection"] : undefined].filter(Boolean).join(" ")}>
          {currentPlans.length > 0 ? versionsTable(currentPlans) : null}
          {archived ? (
            <div className={styles["archive"]}>
              <button
                type="button"
                className={styles["archiveToggle"]}
                aria-expanded={showAll}
                onClick={() => setShowAll((value) => !value)}
                aria-label={intl.formatMessage({ id: "viewer.archive" })}
                aria-describedby={`viewer-archive-count-${patientId}`}
              >
                <FileArchive aria-hidden="true" />
                <span className={styles["archiveHeading"]}>
                  <strong>{intl.formatMessage({ id: "viewer.archive" })}</strong>
                  <Badge id={`viewer-archive-count-${patientId}`} variant="secondary" className="tw-whitespace-nowrap tw-px-2 tw-text-muted-foreground">
                    {intl.formatMessage({ id: "viewer.archive.plans.count" }, { count: archiveCount })}
                  </Badge>
                </span>
                <ChevronDown aria-hidden="true" className={showAll ? styles["archiveExpanded"] : undefined} />
              </button>
              {showAll ? versionsTable(archivedPlans, true) : null}
            </div>
          ) : null}
        </div>
      ) : inline ? (
        legacyActions
      ) : null}
      {dialog}
    </>
  );
  if (inline && !loading && !failed && plans.length === 0 && !legacyActions && !expectedPlanDate) return null;
  if (inline)
    return (
      <ViewCard
        title={intl.formatMessage({ id: currentPlans.some((plan) => plan.state === "pending") ? "redesign.pv.plan.title.pending" : "redesign.pv.plan.title" })}
        testId="rd-pv-plan"
        className={styles["planCard"]}
      >
        {content}
      </ViewCard>
    );
  return (
    <section className={styles["card"]} aria-label={intl.formatMessage({ id: "viewer.title" })}>
      <h2>{intl.formatMessage({ id: "viewer.title" })}</h2>
      {!loading && !failed && plans.length === 0 ? <p>{intl.formatMessage({ id: "viewer.empty" })}</p> : null}
      <div className={styles["buttons"]}>
        <Button
          disabled={loading || plans.length === 0 || failed}
          onClick={() => {
            void openViewer();
          }}
        >
          {intl.formatMessage({ id: "viewer.open" })}
        </Button>
        <Button variant="secondary" disabled={loading} onClick={() => setRevision((value) => value + 1)}>
          {intl.formatMessage({ id: "viewer.refresh" })}
        </Button>
      </div>
      {content}
    </section>
  );
}
